// Typed client for the buyer Market Watch CRUD surface.
//
// Replaces the localStorage path (`propwise.watches.v1`) that
// MarketWatch.tsx previously used — watches now live on the server and
// roam across the user's devices, and the LLM-generated summary
// survives a reload. Mirrors workspaceApi.ts in shape so the existing
// app-wide auth + apiFetch plumbing stays unchanged.
//
// Wire types match backend/internal/watches/store.go field-for-field;
// keep them in sync.

import { apiJson, apiPostJson, apiPatchJson, apiDelete } from "./api";

export interface Watch {
  id: string;
  user_id: string;
  area: string;
  flat_type: string;
  alert_pct: number;
  min_price?: number | null;
  max_price?: number | null;
  summary: string;
  last_checked?: string | null;
  last_alert_psf?: number | null;
  created_at: string;
  updated_at: string;
}

interface WatchListResp {
  watches: Watch[] | null;
}

// listWatches returns the signed-in user's watches, newest first.
// Empty array on no rows; throws ApiError on auth/network failure (the
// caller can degrade to an empty list).
export async function listWatches(): Promise<Watch[]> {
  const resp = await apiJson<WatchListResp>("/watches");
  return resp.watches ?? [];
}

export interface CreateWatchInput {
  area: string;
  flat_type: string;
  alert_pct?: number;
  min_price?: number;
  max_price?: number;
}

// createWatch persists a new watch and returns the full server row
// (carrying generated id + timestamps). The FE inserts this row into
// state on success — symmetric with the post-refresh hydrated shape so
// a refresh shows nothing new (per CLAUDE.md "optimistic UI must equal
// post-refresh UI").
export async function createWatch(input: CreateWatchInput): Promise<Watch> {
  return apiPostJson<Watch>("/watches", input);
}

// PATCH body. Every field optional; nil-leave-unchanged semantics
// matches the server-side UpdateInput. To clear an optional numeric
// (min_price / max_price), send `null`.
export interface UpdateWatchInput {
  area?: string;
  flat_type?: string;
  alert_pct?: number;
  min_price?: number | null;
  max_price?: number | null;
  summary?: string;
  last_checked?: string;
  last_alert_psf?: number;
}

// updateWatch applies a sparse patch and returns the new server row.
// The Refresh-summary flow uses this to persist `summary` +
// `last_checked` after a successful LLM stream.
export async function updateWatch(id: string, patch: UpdateWatchInput): Promise<Watch> {
  return apiPatchJson<Watch>(`/watches/${encodeURIComponent(id)}`, patch);
}

export async function deleteWatch(id: string): Promise<void> {
  await apiDelete(`/watches/${encodeURIComponent(id)}`);
}

export type WatchDraftError = "areaRequired" | "priceRangeInvalid";

// WatchDraftInput is the raw Add-Watch modal shape — like CreateWatchInput
// but alert_pct is always chosen (the modal defaults it), so the modal's
// onSave can require it rather than treat it as optional.
export interface WatchDraftInput {
  area: string;
  flat_type: string;
  alert_pct: number;
  min_price?: number;
  max_price?: number;
}

// validateWatchDraft normalizes raw Add-Watch modal input and enforces
// the same guards the server applies — non-empty area, and max_price >=
// min_price (backend/internal/watches/store.go returns ErrInvalid →
// 400 otherwise). Pure + exported so the modal can show an inline error
// without a round-trip and the rule is unit-testable (PROP-190). Returns
// the cleaned draft, or an i18n error key the modal renders inline.
export function validateWatchDraft(
  raw: WatchDraftInput,
):
  | { ok: true; value: WatchDraftInput }
  | { ok: false; error: WatchDraftError } {
  const area = raw.area.trim();
  if (!area) return { ok: false, error: "areaRequired" };
  const min = raw.min_price;
  const max = raw.max_price;
  if (min != null && max != null && max < min) {
    return { ok: false, error: "priceRangeInvalid" };
  }
  return {
    ok: true,
    value: { area, flat_type: raw.flat_type, alert_pct: raw.alert_pct, min_price: min, max_price: max },
  };
}
